import { Signal } from "@angular/core";
import { ColDef, Column, IRowNode } from "ag-grid-community";

import { MaybeSignal } from "./reactive-inputs/reactive-input.types";

/**
 * Enables bivariant command signatures so context-menu handlers remain ergonomic in consumer code.
 */
type EzBivariantCallback<TArgs extends unknown[], TResult> = {
  bivarianceHack(...args: TArgs): TResult;
}["bivarianceHack"];

/**
 * Distinguishes header and data-cell right-click contexts.
 */
export type EzGridContextMenuTarget = "header" | "cell";

/**
 * Context captured for the currently opened EZ Grid context menu.
 */
export type EzGridContextMenuContext<TData = unknown> = {
  /** Indicates whether the menu was opened from a header or a data cell. */
  target: EzGridContextMenuTarget;
  /** Column definition associated with the clicked header or cell. */
  colDef?: ColDef;
  /** Live AG Grid column associated with the click target. */
  column?: Column;
  /** Resolved column id for the clicked header or cell. */
  columnId?: string;
  /** Row node for the clicked cell. Undefined for header clicks. */
  rowNode?: IRowNode<TData>;
  /** Row data for the clicked cell. Undefined for header clicks. */
  rowData?: TData;
  /** Field associated with the clicked cell or column. */
  field?: string;
  /** Unformatted value from the clicked cell. */
  rawValue?: unknown;
  /** Display value rendered for the clicked cell. */
  displayValue?: string;
  /** Selected text inside the clicked cell when one exists. */
  selectedText?: string;
  /** Mouse event used to position and open the menu. */
  mouseEvent: MouseEvent;
};

/**
 * Built-in EZ Grid menu actions.
 */
export enum EzGridContextMenuBuiltInItemId {
  COPY = "copy",
  COPY_RAW = "copyRaw",
  COPY_SELECTED_TEXT = "copySelectedText",
  PASTE = "paste",
  FILTER_TO = "filterTo",
  CLEAR_FILTER = "clearFilter",
  SORT_ASC = "sortAsc",
  SORT_DESC = "sortDesc",
  PIN_LEFT = "pinLeft",
  PIN_RIGHT = "pinRight",
  UNPIN = "unpin",
  CHOOSE_COLUMNS = "chooseColumns",
  RESET_COLUMNS = "resetColumns",
  RESET_COLUMN = "resetColumn",
}

/**
 * String values accepted for EZ Grid built-in menu actions.
 */
export type EzGridContextMenuBuiltInItem = `${EzGridContextMenuBuiltInItemId}`;

/**
 * Controls how column-level built-ins merge over grid-level built-ins.
 */
export type EzGridContextMenuBuiltInMergeMode = "append" | "prepend" | "replace-grid";

/**
 * Controls how column-level custom items merge over grid-level custom items.
 */
export type EzGridContextMenuCustomItemMergeMode = "append" | "prepend" | "replace-grid";

/**
 * Presentation-only overrides for built-in items.
 */
export type EzGridBuiltInContextMenuItemOverride<TData = unknown> = {
  /** Replaces the built-in's default label template. */
  labelTemplate?: string;
  /** Replaces the built-in's default PrimeNG icon class. */
  icon?: string;
  /** Hides or shows the built-in item without changing its command behavior. */
  visible?: MaybeSignal<boolean>;
  /** Disables the built-in item without hiding it. */
  disabled?: MaybeSignal<boolean>;
};

/**
 * Developer-defined context-menu action.
 */
export type EzGridContextMenuItem<TData = unknown> = {
  /** Stable custom-item identifier used during merge and override resolution. */
  id: string;
  /** Template resolved through `EzTextService` when the menu is built. */
  labelTemplate: string;
  /** Optional PrimeNG icon class. */
  icon?: string;
  /** Hides or shows the item. */
  visible?: MaybeSignal<boolean>;
  /** Disables the item without hiding it. */
  disabled?: MaybeSignal<boolean>;
  /** Executes when the custom item is clicked. */
  command: EzBivariantCallback<[EzGridContextMenuContext<TData>], void | Promise<void>>;
};

/**
 * Explicit visual break inside the custom-item lane.
 */
export type EzGridContextMenuSeparatorItem = {
  /** Marks this custom entry as a separator instead of a clickable command. */
  separator: true;
  /** Optional stable id when a developer wants to identify a separator entry explicitly. */
  id?: string;
};

/**
 * Shared menu-item union used for custom item arrays.
 */
export type EzGridContextMenuCustomEntry<TData = unknown> =
  | EzGridContextMenuItem<TData>
  | EzGridContextMenuSeparatorItem;

/**
 * Factory form for grid-level config. Called once and bound to a persistent menu-context signal.
 *
 * @example
 * ```ts
 * contextMenuConfig: (currentMenuContext) => ({
 *   builtInItems: ["copy"],
 *   customItems: [
 *     {
 *       id: "inspectValue",
 *       labelTemplate: "Inspect {{displayValue}}",
 *       visible: computed(() => !!currentMenuContext()?.displayValue),
 *       command: (ctx) => this.inspect(ctx.rawValue),
 *     },
 *   ],
 * })
 * ```
 */
export type EzGridContextMenuConfigFactory<TData = unknown> = EzBivariantCallback<
  [Signal<EzGridContextMenuContext<TData> | null>],
  EzGridContextMenuConfig<TData>
>;

/**
 * Grid-level EZ Grid context-menu configuration.
 */
export type EzGridContextMenuConfig<TData = unknown> = {
  /** Enables or disables the feature for the whole grid. Supports signals for runtime toggling. */
  enabled?: MaybeSignal<boolean>;
  /** Ordered built-in items enabled for the grid. */
  builtInItems?: EzGridContextMenuBuiltInItem[];
  /** Presentation-only overrides for built-in items. */
  builtInItemOverrides?: Partial<Record<EzGridContextMenuBuiltInItem, EzGridBuiltInContextMenuItemOverride<TData>>>;
  /** Static custom entries appended after built-ins. */
  customItems?: EzGridContextMenuCustomEntry<TData>[];
};

/**
 * Accepts either a static config object or a factory that can read `currentMenuContext`.
 */
export type EzGridContextMenuConfigInput<TData = unknown> =
  | EzGridContextMenuConfig<TData>
  | EzGridContextMenuConfigFactory<TData>;

/**
 * Column-level override metadata added to AG Grid `ColDef` via module augmentation.
 *
 * Column-level config is intentionally plain-object only in V1.
 * Context-aware logic should live in the grid-level factory form.
 */
export type EzGridColumnContextMenuConfig<TData = unknown> = {
  /** Enables or disables the menu for one column. */
  enabled?: boolean;
  /** Ordered built-in items contributed by the column override. */
  builtInItems?: EzGridContextMenuBuiltInItem[];
  /** Presentation-only built-in overrides applied for this column. */
  builtInItemOverrides?: Partial<Record<EzGridContextMenuBuiltInItem, EzGridBuiltInContextMenuItemOverride<TData>>>;
  /** Static custom entries contributed by this column override. */
  customItems?: EzGridContextMenuCustomEntry<TData>[];
  /** Controls how column built-ins merge over inherited grid built-ins. */
  builtInMergeMode?: EzGridContextMenuBuiltInMergeMode;
  /** Controls how column custom items merge over inherited grid custom items. */
  customItemMergeMode?: EzGridContextMenuCustomItemMergeMode;
};

/**
 * Persisted column state saved under the grid `settingsKey`.
 */
export type EzGridSavedColumnState = {
  /** AG Grid column id. */
  columnId: string;
  /** Zero-based display order captured from the live grid. */
  order: number;
  /** Hidden state override. */
  hidden?: boolean;
  /** Persisted width when the column supports width persistence. */
  width?: number;
  /** Persisted flex factor. `0` disables flex so an explicit width wins on restore; a positive number re-applies the flex factor. */
  flex?: number | null;
  /** Persisted pin state. */
  pinned?: "left" | "right";
  /** Persisted sort direction. */
  sort?: "asc" | "desc";
};

/**
 * Versioned saved payload for Choose Columns / reset behavior.
 */
export type EzGridSavedColumnSettings = {
  /** Payload version used for future migration support. */
  version: 1;
  /** Persisted per-column state entries. */
  columns: EzGridSavedColumnState[];
};
